import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { DetailsHeader } from "./detailHeader";
import { Button } from "@shared/ui/button";
import { Pencil, FileText, Plus, ArrowLeft, NotebookPen } from "lucide-react";
import { Tabs } from "@shared/ui/tabs";

import { ResourcesTab } from "./tabs/meters/landing";
import { useGetRouteData } from "../../hooks";
import { UpdateReadCycleModal } from "./updateReadCycleModal";
import { OverviewTab } from "./tabs/Overview";
import { AddPremiseModal } from "./addPremiseModal";
import { PremisesTab } from "./tabs/premise/premiseList";

import { PageHeader } from "@shared/components/PageHeader";
import { NotesTab } from "@features/mx/shared/components/NotesTab";
import { ActivityLogTab } from "@features/mx/shared/components/ActivityLog";
import { NoteDialog } from "@features/mx/shared/components/NoteDialog";

const RouteDetails = () => {
  const { route_id } = useParams();
  const navigate = useNavigate();
  const { data: routeDetailsData, refetch: refetchRouteDetails } =
    useGetRouteData(route_id);
  const [isUpdateReadCycleOpen, setIsUpdateReadCycleOpen] = useState(false); // ✅ Modal state
  const [isAddPremiseOpen, setIsAddPremiseOpen] = useState(false); // ✅ new state
  const [isAddNoteOpen, setIsAddNoteOpen] = useState(false); // ✅ Note modal state
  const source = "0";
  const handleMoveRoutes = (destinationCycleId: string) => {};

  const tabComponents = {
    overview: {
      label: "Overview",
      shortLabel: "overview",
      component: <OverviewTab routeDetails={routeDetailsData} />,
    },
    premises: {
      label: "Premises",
      shortLabel: "premises",
      component: <PremisesTab routeDetails={routeDetailsData} />,
    },
    resources: {
      label: "Resources",
      shortLabel: "resources",
      component: <ResourcesTab />,
    },
    notes: {
      label: "Notes",
      shortLabel: "notes",
      component: <NotesTab requestId={route_id} source={source} />,
    },
    logs: {
      label: "Activity Log",
      shortLabel: "logs",
      component: <ActivityLogTab entityId={route_id} entityType={source} />,
    },
  };

  const urlMapping = {
    overview: "overview",
    premises: "premises",
    meters: "meters",
    logs: "logs",
    notes: "notes",
  };

  // ✅ Moved QuickActions here so it can access modal open handler
  const QuickActions = () => {
    return (
      <div className="bg-white rounded-xl border border-gray-200/80 p-5 shadow-xs flex flex-col space-y-3">
        <h3 className="text-sm font-bold text-gray-900 tracking-wide">
          Quick Actions
        </h3>

        <div className="space-y-2 flex flex-col">
          <Button
            variant="outline"
            onClick={() => navigate(`/mx/routeManagement/edit/${route_id}`)}
            className="w-full justify-start gap-2.5"
          >
            <Pencil className="h-4 w-4 text-gray-500" />
            <span>Edit Route</span>
          </Button>

          {/* ✅ Open modal on click */}
          <Button
            variant="outline"
            onClick={() => setIsUpdateReadCycleOpen(true)}
            className="w-full justify-start gap-2.5"
          >
            <FileText className="h-4 w-4 text-gray-500" />
            <span>Reassign</span>
          </Button>

          <Button
            variant="outline"
            onClick={() => setIsAddPremiseOpen(true)}
            className="w-full justify-start gap-2.5"
          >
            <Plus className="h-4 w-4 text-gray-500" />
            <span>Add Premises</span>
          </Button>

          <Button
            variant="outline"
            onClick={() => setIsAddNoteOpen(true)}
            className="w-full justify-start gap-2.5"
          >
            <NotebookPen className="h-4 w-4 text-gray-500" />
            <span>Add Note</span>
          </Button>
        </div>
      </div>
    );
  };

  return (
    <div>
      <div className="mb-4 flex items-center gap-4">
        {/* Back Arrow */}
        <ArrowLeft
          className="w-5 h-5 cursor-pointer text-gray-700"
          onClick={() => navigate("/mx/routeManagement/")}
        />

        {/* Page Header */}
        <div className="flex flex-col">
          <PageHeader
            title="View Route"
            subtitle="Track performance and progress of your resource reading route"
          />
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 items-start">
        {/* Left Column - Details */}
        <div className="lg:col-span-3">
          <DetailsHeader routeDetails={routeDetailsData} />
        </div>

        {/* Right Column - Quick Actions */}
        <div className="lg:col-span-1">
          <QuickActions />
        </div>
      </div>

      <div className="pt-4">
        <Tabs
          defaultValue="overview"
          urlMapping={urlMapping}
          tabComponents={tabComponents}
          tabsListFullWidth={true}
          tabsListClassName="grid grid-cols-5"
        />
      </div>

      {/* ✅ Include the dialog */}
      <UpdateReadCycleModal
        open={isUpdateReadCycleOpen}
        onOpenChange={setIsUpdateReadCycleOpen}
        selectedRoutes={[route_id || ""]}
        onMoveRoutes={handleMoveRoutes}
        estimatedMeters={50} // Example count, replace dynamically if needed
      />
      <AddPremiseModal
        routeDetails={routeDetailsData}
        open={isAddPremiseOpen}
        onOpenChange={setIsAddPremiseOpen}
        onSuccess={refetchRouteDetails}
      />
      <NoteDialog
        isOpen={isAddNoteOpen}
        onClose={() => setIsAddNoteOpen(false)}
        itemId={route_id || ""}
        source={source}
      />
    </div>
  );
};

export default RouteDetails;
